import {
  mockCatalogue,
  mockCreateSubscription,
  mockEndSubscription,
  mockMrr,
  mockSaveCatalogueItem,
  mockSubscriptionsFor,
} from './catalogue';
import { runCostQuery } from '../costs/mockData';
import { mockContacts, mockCreateContact, mockDeleteContact, mockUpdateContact } from './contacts';
import {
  mockCreateFlag,
  mockDeleteFlag,
  mockFlags,
  mockUpdateFlag,
  mockUpdateFlagEnvironment,
} from './flags';
import type { IFlagEnvironmentInput, IFlagInput } from '../types';
import type { IContactInput, IProvisionTenantInput, IStartRunInput } from '../types';
import {
  mockCreateTenant,
  mockPatchStep,
  mockProvisionedTenants,
  mockProvisioningRuns,
  mockStartRun,
} from './provisioning';
import {
  mockBillingProfile,
  mockCreateInvoice,
  mockInvoice,
  mockInvoicePreview,
  mockInvoices,
  mockIssueInvoice,
  mockMarkPaid,
  mockSaveBillingProfile,
  mockSaveTenantBilling,
  mockSendInvoice,
  mockTenantBilling,
  mockUpdateInvoice,
  mockVoidInvoice,
} from './invoices';
import type {
  IBillingProfile,
  IInvoiceInput,
  ISendInvoiceInput,
  ITenantBillingInput,
} from '../types';
import { fromSearchParams } from '../costs/model';
import {
  mockAskFeedback,
  mockCreateAsk,
  mockDeleteConversation,
  mockGetAsk,
  mockListConversations,
  mockThread,
} from './ask';
import type { IAskFeedbackInput, IAskInput } from '../types';
import type { Environment } from '../constants';
import type {
  IAuditEntry,
  ISubscription,
  ISubscriptionInput,
  ITenantExtension,
  ITenantSummary,
  ITenantUser,
} from '../types';

const base = (environment: Environment): ITenantSummary[] => [
  {
    id: 'c1f3a8e2-7b4d-4f9a-9e21-3d5b7a6c8e10',
    name: 'azzurri',
    display_name: 'Azzurri Group',
    status: 'active',
    environment,
    workos_org_id: 'org_01K072BNP83A74M0MVPPM69CG2',
    users: 56,
    brands: 4,
    venues: 420,
    addons: ['assure65', 'deliveroo', 'uber_eats', 'canva'],
    base_plan: null,
    mrr: null,
    last_publish_at: '2026-10-22T07:40:00Z',
    health: 'warning',
    failed_events_24h: 0,
    notes: null,
    secret_present: true,
    stats_error: null,
    entitled: [],
  },
  {
    id: '3f6d0b2e-5a1c-4d7e-9b2a-6c8e1f4a7d93',
    name: 'burgerking',
    display_name: 'Burger King UK',
    status: 'active',
    environment,
    workos_org_id: 'org_01KR0TJ6Y23FTPPC229W2P2SWC',
    users: 9,
    brands: 1,
    venues: 300,
    addons: ['oracle', 'fourth'],
    base_plan: 'Standard',
    mrr: 4800,
    last_publish_at: '2026-10-21T18:05:00Z',
    health: 'ok',
    failed_events_24h: 0,
    notes: null,
    secret_present: true,
    stats_error: null,
    entitled: [],
  },
  {
    id: '8a2d6c41-9e3f-4b7a-8c15-2f6e9d0b4a77',
    name: 'hallandwoodhouse',
    display_name: 'Hall & Woodhouse',
    status: 'active',
    environment,
    workos_org_id: 'org_01KAZX05JJ27SXSS02DP6N5K01',
    users: 16,
    brands: 1,
    venues: 180,
    addons: ['zonal', 'fourth'],
    base_plan: 'Standard',
    mrr: 2100,
    last_publish_at: '2026-10-20T11:12:00Z',
    health: 'ok',
    failed_events_24h: 0,
    notes: null,
    secret_present: true,
    stats_error: null,
    entitled: [],
  },
  {
    id: 'd4b7e9a1-2c6f-4e38-b5a9-7f1c3e8d2b64',
    name: 'bigtablegroup',
    display_name: 'Big Table Group',
    status: 'active',
    environment,
    workos_org_id: 'org_01KPAKBXJX51NNNPPCJZV548Q0',
    users: 4,
    brands: 3,
    venues: 150,
    addons: ['deliveroo'],
    base_plan: 'Starter',
    mrr: 900,
    last_publish_at: null,
    health: 'error',
    failed_events_24h: 2,
    notes: null,
    secret_present: true,
    stats_error: null,
    entitled: [],
  },
  {
    id: '6e9c2b85-4d1a-4f7e-a3c8-9b5d7e1f0a23',
    name: 'stonegate',
    display_name: 'Stonegate',
    status: 'suspended',
    environment,
    workos_org_id: 'org_01K1X6J407KP5QPT177QB3EEAK',
    users: 1,
    brands: 1,
    venues: 0,
    addons: ['zonal'],
    base_plan: null,
    mrr: null,
    last_publish_at: null,
    health: 'unknown',
    failed_events_24h: 0,
    notes: null,
    secret_present: true,
    stats_error: null,
    entitled: [],
  },
];

const nonProdExtras = (environment: Environment): ITenantSummary[] => [
  {
    id: 'b2e5f8c3-1a7d-4c9e-8f24-6d3a9b7e5c11',
    name: 'heartwood',
    display_name: 'Heartwood',
    status: 'provisioning',
    environment,
    workos_org_id: 'org_01M2952MRM7Z8HGHCRY8SRT4CB',
    users: 6,
    brands: 1,
    venues: 12,
    addons: ['procure_wizard'],
    base_plan: null,
    mrr: null,
    last_publish_at: null,
    health: 'unknown',
    failed_events_24h: 0,
    notes: null,
    secret_present: true,
    stats_error: null,
    entitled: [],
  },
  {
    id: 'f7a1c4d9-8b2e-4a6f-9c3d-1e5b7a9d2f48',
    name: 'demo',
    display_name: 'Demo',
    status: 'active',
    environment,
    workos_org_id: 'org_01K88F14FD9KMDJNJD6MG6XKJK',
    users: 13,
    brands: 2,
    venues: 8,
    addons: [],
    base_plan: 'Starter',
    mrr: 0,
    last_publish_at: '2026-10-19T09:00:00Z',
    health: 'ok',
    failed_events_24h: 0,
    notes: null,
    secret_present: true,
    stats_error: null,
    entitled: [],
  },
];

export const fixtureTenants = (environment: Environment): ITenantSummary[] =>
  environment === 'prod'
    ? base(environment)
    : [...base(environment), ...nonProdExtras(environment)];

/** Fixtures plus any tenant created through the mock "New tenant" form in this browser. */
const allTenants = (environment: Environment): ITenantSummary[] => [
  ...fixtureTenants(environment),
  ...mockProvisionedTenants(environment),
];

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const mockMetric = (environment: Environment, params: URLSearchParams) => {
  const metric = params.get('metric') ?? 'venues';
  const groupBy = params.get('group_by') ?? 'tenant';
  const range = params.get('range') ?? '30d';
  const tenants = fixtureTenants(environment);
  if (metric === 'publishes' || metric === 'failures') {
    const days = Number(range.replace('d', ''));
    const series = fixturePublishes().slice(-Math.min(days, 30));
    if (groupBy === 'tenant') {
      const points = tenants.map((tenant, index) => ({
        key: tenant.name,
        label: tenant.display_name,
        value:
          series.reduce(
            (total, row) => total + (metric === 'failures' ? row.failures : row.publishes),
            0,
          ) >> index,
      }));
      return {
        metric,
        group_by: 'tenant',
        range,
        unit: 'count',
        points: points.sort((a, b) => b.value - a.value),
      };
    }
    return {
      metric,
      group_by: 'day',
      range,
      unit: 'count',
      points: series.map((row) => ({
        key: row.day,
        label: row.day,
        value: metric === 'failures' ? row.failures : row.publishes,
        series: { publishes: row.publishes, failures: row.failures },
      })),
    };
  }
  if (groupBy === 'addon') {
    const counts = new Map<string, number>();
    tenants.forEach(({ addons }) =>
      addons.forEach((code) => counts.set(code, (counts.get(code) ?? 0) + 1)),
    );
    const points = Array.from(counts.entries())
      .map(([code, value]) => ({ key: code, label: code.replace('_', ' '), value }))
      .sort((a, b) => b.value - a.value);
    return { metric, group_by: 'addon', range: null, unit: 'count', points };
  }
  const valueOf = (tenant: ITenantSummary): number => {
    if (metric === 'mrr') return tenant.mrr ?? 0;
    if (metric === 'users') return tenant.users;
    if (metric === 'brands') return tenant.brands;
    if (metric === 'addons') return tenant.addons.length;
    return tenant.venues;
  };
  const points = tenants
    .map((tenant) => ({ key: tenant.name, label: tenant.display_name, value: valueOf(tenant) }))
    .sort((a, b) => b.value - a.value);
  return {
    metric,
    group_by: 'tenant',
    range: null,
    unit: metric === 'mrr' ? 'gbp' : 'count',
    points,
  };
};

const NAMES = [
  'Ava Patel',
  'Ben Okafor',
  'Chloe Martin',
  'Dan Reyes',
  'Esme Larsson',
  'Finn Walsh',
  'Grace Kim',
  'Hugo Dubois',
];

export const fixtureUsers = (tenant: ITenantSummary): ITenantUser[] =>
  Array.from({ length: Math.min(tenant.users, 8) }, (_, index) => {
    const [first, last] = NAMES[index % NAMES.length].split(' ');
    return {
      id: `user_${tenant.name}_${index}`,
      email: `${first.toLowerCase()}.${last.toLowerCase()}@${tenant.name}.example`,
      first_name: first,
      last_name: last,
      role: index === 0 ? 'admin' : 'member',
      status: index === 5 ? 'inactive' : 'active',
      created_at: `2026-0${(index % 8) + 1}-1${index}T09:00:00Z`,
      last_sign_in_at: index % 3 === 2 ? null : `2026-10-${10 + index}T08:30:00Z`,
      email_verified: index !== 4,
    };
  });

export const fixtureExtensions = (tenant: ITenantSummary): ITenantExtension[] =>
  tenant.addons.map((code, index) => ({
    extension_id: `ext_${tenant.name}_${index}`,
    brand_id: `brand_${tenant.name}_${index % Math.max(1, tenant.brands)}`,
    brand_name:
      tenant.brands > 1
        ? `${tenant.display_name} brand ${(index % tenant.brands) + 1}`
        : tenant.display_name,
    addon_name: `Publish to ${code.replace('_', ' ')}`,
    addon_code: code,
    created_at: `2026-0${(index % 8) + 1}-05T10:00:00Z`,
    instances: [
      {
        id: `inst_${tenant.name}_${index}`,
        name: `${code.replace('_', ' ')} UK`,
        created_at: `2026-0${(index % 8) + 1}-06T10:00:00Z`,
      },
    ],
  }));

export const fixtureSubscriptions = (tenant: ITenantSummary): ISubscription[] =>
  tenant.mrr
    ? [
        {
          id: `sub_${tenant.name}_base`,
          tenant: tenant.name,
          product_code: 'base',
          price: Math.round(tenant.mrr * 0.7),
          currency: 'GBP',
          period: 'monthly',
          quantity: null,
          starts_on: '2026-05-01',
          ends_on: null,
          reason: null,
          created_at: '2026-05-01T00:00:00Z',
          created_by: 'alex',
        },
        ...tenant.addons.slice(0, 1).map((code) => ({
          id: `sub_${tenant.name}_${code}`,
          tenant: tenant.name,
          product_code: `addon.${code}`,
          price: Math.round((tenant.mrr ?? 0) * 0.3),
          currency: 'GBP',
          period: 'monthly' as const,
          quantity: null,
          starts_on: '2026-06-01',
          ends_on: null,
          reason: null,
          created_at: '2026-06-01T00:00:00Z',
          created_by: 'alex',
        })),
      ]
    : [];

export const fixtureAudit = (tenant: ITenantSummary): IAuditEntry[] => [
  {
    id: `aud_${tenant.name}_1`,
    at: '2026-10-21T14:12:00Z',
    staff_uid: 'user_alex',
    staff_email: 'alex.satur@weareopenr.com',
    action: 'subscription.create',
    target_type: 'subscription',
    target_id: `sub_${tenant.name}_base`,
    tenant: tenant.name,
    before: null,
    after: { product_code: 'base' },
    request_id: 'req_1',
  },
  {
    id: `aud_${tenant.name}_2`,
    at: '2026-10-20T09:40:00Z',
    staff_uid: 'user_alex',
    staff_email: 'alex.satur@weareopenr.com',
    action: 'tenant.update',
    target_type: 'tenant',
    target_id: tenant.name,
    tenant: tenant.name,
    before: { notes: null },
    after: { notes: 'Onboarded by Alex' },
    request_id: 'req_2',
  },
];

export const mockGet = async <T>(environment: Environment, path: string): Promise<T> => {
  await delay(250);
  if (path === '/tenants') {
    const tenants = allTenants(environment);
    return {
      data: tenants.map((tenant) => ({
        ...tenant,
        mrr: Math.round(mockMrr(tenants, tenant.name)) || null,
      })),
    } as T;
  }
  if (path.startsWith('/costs?')) {
    const tenants = fixtureTenants(environment);
    const venues = tenants.reduce((total, tenant) => total + (tenant.venues ?? 0), 0);
    return {
      data: runCostQuery(
        fromSearchParams(new URLSearchParams(path.slice('/costs?'.length))),
        venues,
      ),
    } as T;
  }
  if (path === '/flags') {
    return { data: mockFlags() } as T;
  }
  if (path.startsWith('/ask/conversations?')) {
    const tenant =
      new URLSearchParams(path.slice('/ask/conversations?'.length)).get('tenant') ?? '';
    return { data: mockListConversations(tenant) } as T;
  }
  if (path.startsWith('/ask/conversations/')) {
    return { data: mockThread(decodeURIComponent(path.slice('/ask/conversations/'.length))) } as T;
  }
  if (path.startsWith('/ask/')) {
    return { data: mockGetAsk(decodeURIComponent(path.slice('/ask/'.length))) } as T;
  }
  if (path === '/billing/profile') {
    return { data: mockBillingProfile() } as T;
  }
  if (path === '/invoices') {
    return { data: mockInvoices(environment) } as T;
  }
  const previewMatch = /^\/tenants\/([^/?]+)\/invoices\/preview(?:\?(.*))?$/.exec(path);
  if (previewMatch) {
    const tenantName = decodeURIComponent(previewMatch[1]);
    const tenant = allTenants(environment).find(({ name }) => name === tenantName);
    if (!tenant) throw { message: 'Tenant not found', status: 404 };
    const params = Object.fromEntries(new URLSearchParams(previewMatch[2] ?? ''));
    const names = new Map(
      mockCatalogue(fixtureTenants(environment)).map((item) => [item.code, item.name]),
    );
    return {
      data: mockInvoicePreview(
        environment,
        tenantName,
        mockSubscriptionsFor(fixtureTenants(environment), tenantName),
        names,
        params,
      ),
    } as T;
  }
  const invoiceMatch = /^\/tenants\/([^/?]+)\/invoices\/([^/?]+)$/.exec(path);
  if (invoiceMatch) {
    return {
      data: mockInvoice(
        environment,
        decodeURIComponent(invoiceMatch[1]),
        decodeURIComponent(invoiceMatch[2]),
      ),
    } as T;
  }
  const pdfMatch = /^\/tenants\/([^/?]+)\/invoices\/([^/?]+)\/pdf(?:\?.*)?$/.exec(path);
  if (pdfMatch) {
    const tenantName = decodeURIComponent(pdfMatch[1]);
    const id = decodeURIComponent(pdfMatch[2]);
    const invoice = mockInvoice(environment, tenantName, id);
    return {
      data: {
        url: `/tenants/${encodeURIComponent(tenantName)}/invoices/${encodeURIComponent(
          id,
        )}/view?print=1`,
        filename: `openr-invoice-${invoice.number}.pdf`,
        expires_in: 600,
      },
    } as T;
  }
  if (path === '/catalogue') {
    return { data: mockCatalogue(fixtureTenants(environment)) } as T;
  }
  const tenantMatch = /^\/tenants\/([^/?]+)(?:\/([a-z]+))?$/.exec(path);
  if (tenantMatch) {
    const tenant = allTenants(environment).find(
      ({ name }) => name === decodeURIComponent(tenantMatch[1]),
    );
    if (!tenant) {
      throw { message: 'Tenant not found', status: 404 };
    }
    const section = tenantMatch[2];
    if (!section)
      return {
        data: {
          ...tenant,
          mrr: Math.round(mockMrr(fixtureTenants(environment), tenant.name)) || null,
        },
      } as T;
    if (section === 'runs') return { data: mockProvisioningRuns(environment, tenant.name) } as T;
    if (section === 'billing') return { data: mockTenantBilling(environment, tenant.name) } as T;
    if (section === 'invoices') return { data: mockInvoices(environment, tenant.name) } as T;
    if (section === 'users')
      return { data: { organisation_id: tenant.workos_org_id, users: fixtureUsers(tenant) } } as T;
    if (section === 'contacts') return { data: mockContacts(tenant.name) } as T;
    if (section === 'extensions') return { data: fixtureExtensions(tenant) } as T;
    if (section === 'subscriptions')
      return { data: mockSubscriptionsFor(fixtureTenants(environment), tenant.name) } as T;
  }
  const auditMatch = /^\/audit\?tenant=([^&]+)/.exec(path);
  if (auditMatch) {
    const tenant = fixtureTenants(environment).find(
      ({ name }) => name === decodeURIComponent(auditMatch[1]),
    );
    return { data: tenant ? fixtureAudit(tenant) : [] } as T;
  }
  if (path.startsWith('/metrics?')) {
    return {
      data: mockMetric(environment, new URLSearchParams(path.slice('/metrics?'.length))),
    } as T;
  }
  throw { message: `No fixture for ${path}`, status: 404 };
};

export interface DailyPoint {
  day: string;
  publishes: number;
  failures: number;
}

/** Thirty days of publish activity, deterministic so screenshots and tests are stable. */
export const fixturePublishes = (): DailyPoint[] =>
  Array.from({ length: 30 }, (_, index) => {
    const date = new Date(Date.UTC(2026, 9, 22 - (29 - index)));
    const weekday = date.getUTCDay();
    const base = weekday === 0 || weekday === 6 ? 6 : 18;
    const wobble = ((index * 7) % 11) - 5;
    return {
      day: date.toISOString().slice(0, 10),
      publishes: Math.max(0, base + wobble),
      failures: index % 9 === 4 ? 2 : index % 6 === 1 ? 1 : 0,
    };
  });

export interface MonthlyCost {
  month: string;
  dev: number;
  staging: number;
  prod: number;
  other: number;
}

/** Audited Cost Explorer totals, USD, April to September 2026 (docs/management-layer/05-aws-costs-today.md). */
export const fixtureAwsCost = (): MonthlyCost[] => [
  { month: 'Apr', dev: 2182, staging: 1383, prod: 2009, other: 413 },
  { month: 'May', dev: 2367, staging: 1338, prod: 1994, other: 914 },
  { month: 'Jun', dev: 2208, staging: 1087, prod: 1735, other: 912 },
  { month: 'Jul', dev: 2324, staging: 1121, prod: 1764, other: 920 },
  { month: 'Aug', dev: 2427, staging: 1285, prod: 1723, other: 922 },
  { month: 'Sep', dev: 1669, staging: 740, prod: 1377, other: 894 },
];

export const mockSend = async <T>(
  environment: Environment,
  path: string,
  method: string,
  body?: unknown,
): Promise<T> => {
  await delay(200);
  if (path.startsWith('/ask/conversations/') && method === 'DELETE') {
    return {
      data: mockDeleteConversation(decodeURIComponent(path.slice('/ask/conversations/'.length))),
    } as T;
  }
  if (path === '/ask' && method === 'POST') {
    return { data: mockCreateAsk(body as IAskInput) } as T;
  }
  if (path.startsWith('/ask/') && path.endsWith('/feedback') && method === 'POST') {
    const id = path.slice('/ask/'.length, -'/feedback'.length);
    return { data: mockAskFeedback(decodeURIComponent(id), body as IAskFeedbackInput) } as T;
  }
  const tenants = fixtureTenants(environment);
  if (path === '/billing/profile' && method === 'PUT') {
    return { data: mockSaveBillingProfile(body as IBillingProfile) } as T;
  }
  const billingMatch = /^\/tenants\/([^/]+)\/billing$/.exec(path);
  if (billingMatch && method === 'PUT') {
    return {
      data: mockSaveTenantBilling(
        environment,
        decodeURIComponent(billingMatch[1]),
        body as ITenantBillingInput,
      ),
    } as T;
  }
  const invoicesMatch = /^\/tenants\/([^/]+)\/invoices$/.exec(path);
  if (invoicesMatch && method === 'POST') {
    const tenantName = decodeURIComponent(invoicesMatch[1]);
    const tenant = allTenants(environment).find(({ name }) => name === tenantName);
    if (!tenant) throw { message: 'Tenant not found', status: 404 };
    return {
      data: mockCreateInvoice(environment, tenantName, tenant.display_name, body as IInvoiceInput),
    } as T;
  }
  const invoiceActionMatch =
    /^\/tenants\/([^/]+)\/invoices\/([^/]+)(?:\/(issue|send|paid|void))?$/.exec(path);
  if (invoiceActionMatch) {
    const tenantName = decodeURIComponent(invoiceActionMatch[1]);
    const id = decodeURIComponent(invoiceActionMatch[2]);
    const action = invoiceActionMatch[3];
    if (!action && method === 'PUT') {
      return {
        data: mockUpdateInvoice(environment, tenantName, id, body as Partial<IInvoiceInput>),
      } as T;
    }
    if (action === 'issue') return { data: mockIssueInvoice(environment, tenantName, id) } as T;
    if (action === 'send')
      return { data: mockSendInvoice(environment, tenantName, id, body as ISendInvoiceInput) } as T;
    if (action === 'paid') {
      return {
        data: mockMarkPaid(environment, tenantName, id, (body as { paid_on: string }).paid_on),
      } as T;
    }
    if (action === 'void') {
      return {
        data: mockVoidInvoice(
          environment,
          tenantName,
          id,
          (body as { reason: string | null }).reason,
        ),
      } as T;
    }
  }
  if (path === '/tenants' && method === 'POST') {
    return {
      data: mockCreateTenant(environment, allTenants(environment), body as IProvisionTenantInput),
    } as T;
  }
  const runsMatch = /^\/tenants\/([^/]+)\/runs$/.exec(path);
  if (runsMatch && method === 'POST') {
    return {
      data: mockStartRun(environment, decodeURIComponent(runsMatch[1]), body as IStartRunInput),
    } as T;
  }
  const stepMatch = /^\/tenants\/([^/]+)\/runs\/([^/]+)\/steps\/([^/]+)$/.exec(path);
  if (stepMatch && method === 'PATCH') {
    const { status } = body as { status: Parameters<typeof mockPatchStep>[4] };
    return {
      data: mockPatchStep(
        environment,
        decodeURIComponent(stepMatch[1]),
        decodeURIComponent(stepMatch[2]),
        decodeURIComponent(stepMatch[3]),
        status,
      ),
    } as T;
  }
  if (path === '/flags' && method === 'POST') {
    return { data: mockCreateFlag(body as IFlagInput) } as T;
  }
  const flagEnvMatch = /^\/flags\/([^/]+)\/environments\/([^/]+)$/.exec(path);
  if (flagEnvMatch && method === 'PUT') {
    return {
      data: mockUpdateFlagEnvironment(
        decodeURIComponent(flagEnvMatch[1]),
        decodeURIComponent(flagEnvMatch[2]),
        body as IFlagEnvironmentInput,
      ),
    } as T;
  }
  const flagMatch = /^\/flags\/([^/]+)$/.exec(path);
  if (flagMatch && method === 'PUT') {
    return { data: mockUpdateFlag(decodeURIComponent(flagMatch[1]), body as IFlagInput) } as T;
  }
  if (flagMatch && method === 'DELETE') {
    mockDeleteFlag(decodeURIComponent(flagMatch[1]));
    return undefined as T;
  }
  const contactsMatch = /^\/tenants\/([^/]+)\/contacts(?:\/([^/]+))?$/.exec(path);
  if (contactsMatch) {
    const tenant = decodeURIComponent(contactsMatch[1]);
    const id = contactsMatch[2] ? decodeURIComponent(contactsMatch[2]) : null;
    if (method === 'POST') return { data: mockCreateContact(tenant, body as IContactInput) } as T;
    if (method === 'PUT' && id)
      return { data: mockUpdateContact(tenant, id, body as IContactInput) } as T;
    if (method === 'DELETE' && id) {
      mockDeleteContact(tenant, id);
      return undefined as T;
    }
  }
  const catalogueMatch = /^\/catalogue\/([^/]+)$/.exec(path);
  if (catalogueMatch && method === 'PUT') {
    return {
      data: mockSaveCatalogueItem(body as Parameters<typeof mockSaveCatalogueItem>[0]),
    } as T;
  }
  const createMatch = /^\/tenants\/([^/]+)\/subscriptions$/.exec(path);
  if (createMatch && method === 'POST') {
    return {
      data: mockCreateSubscription(
        tenants,
        decodeURIComponent(createMatch[1]),
        body as ISubscriptionInput,
      ),
    } as T;
  }
  const endMatch = /^\/tenants\/([^/]+)\/subscriptions\/([^/]+)\/end$/.exec(path);
  if (endMatch && method === 'POST') {
    const { ends_on: endsOn, reason } = body as { ends_on: string; reason: string | null };
    return {
      data: mockEndSubscription(
        tenants,
        decodeURIComponent(endMatch[1]),
        decodeURIComponent(endMatch[2]),
        endsOn,
        reason,
      ),
    } as T;
  }
  throw { message: `No fixture for ${method} ${path}`, status: 404 };
};
